import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { Draft, ID } from '@proofroom/schema';
import { documents, type DraftDiffResult } from '../api/endpoints';
import { wordDiff } from '../model/wordDiff';
import { EmptyState, ErrorState, Spinner } from './ui';

export interface ComparePanelProps {
  documentId: ID;
  drafts: Draft[];
  currentDraftId?: ID;
}

/**
 * "to" is the current draft (or the newest, if none is current). "from" is the most recently
 * approved draft other than "to" when one exists, else whatever draft comes right before "to".
 */
export function defaultCompareSelection(drafts: Draft[], currentDraftId?: ID): { from?: ID; to?: ID } {
  const byNumberDesc = [...drafts].sort((a, b) => b.number - a.number);
  const to = byNumberDesc.find((d) => d.id === currentDraftId) ?? byNumberDesc[0];
  if (!to) return {};
  const approved = byNumberDesc.filter((d) => d.approvedAt && d.id !== to.id);
  const previous = byNumberDesc.find((d) => d.number < to.number);
  const from = approved[0] ?? previous;
  return { from: from?.id, to: to.id };
}

export function ComparePanel({ documentId, drafts, currentDraftId }: ComparePanelProps) {
  // a pruned draft has no snapshot left, so it is not offered on either side of the diff
  const comparable = drafts.filter((d) => !d.prunedAt);
  const byNumberDesc = [...comparable].sort((a, b) => b.number - a.number);
  const defaults = defaultCompareSelection(comparable, currentDraftId);
  const [fromId, setFromId] = useState<ID | undefined>(defaults.from);
  const [toId, setToId] = useState<ID | undefined>(defaults.to);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState<{ from: ID; to: ID } | undefined>();

  const q = useQuery({
    queryKey: ['documents', documentId, 'diff', submitted?.from, submitted?.to],
    queryFn: () => documents.diff(documentId, submitted!.from, submitted!.to),
    enabled: Boolean(submitted),
  });

  if (byNumberDesc.length < 2) return null;

  const runCompare = () => {
    setOpen(true);
    if (fromId && toId) setSubmitted({ from: fromId, to: toId });
  };

  return (
    <div className="card compare-panel">
      <div className="compare-controls">
        <label className="compare-field">
          From
          <select className="input" aria-label="Compare from draft" value={fromId ?? ''} onChange={(e) => setFromId(e.target.value)}>
            {byNumberDesc.map((d) => <option key={d.id} value={d.id}>{`Draft ${d.number}${d.approvedAt ? ' (approved)' : ''}`}</option>)}
          </select>
        </label>
        <label className="compare-field">
          To
          <select className="input" aria-label="Compare to draft" value={toId ?? ''} onChange={(e) => setToId(e.target.value)}>
            {byNumberDesc.map((d) => <option key={d.id} value={d.id}>{`Draft ${d.number}`}</option>)}
          </select>
        </label>
        <button className="btn" disabled={!fromId || !toId} onClick={runCompare}>Compare</button>
      </div>
      {open && (
        <div className="compare-results">
          {q.isPending && <Spinner label="Comparing drafts" />}
          {q.isError && <ErrorState title="Could not compare these drafts" error={q.error} retry={() => void q.refetch()} />}
          {q.data && <CompareResults documentId={documentId} diff={q.data} />}
        </div>
      )}
    </div>
  );
}

function CompareResults({ documentId, diff }: { documentId: ID; diff: DraftDiffResult }) {
  const stories = diff.stories.filter((s) => s.paragraphs.length > 0);
  if (stories.length === 0) {
    return <EmptyState title="No changes" glyph="=">{`Draft ${diff.to.number} matches draft ${diff.from.number}.`}</EmptyState>;
  }
  return (
    <>
      {stories.map((s) => (
        <div key={s.storyId} className="compare-story">
          <div className="compare-story-head">
            <span>{s.label ?? `Story ${s.storyId}`}</span>
            <Link className="btn sm ghost" to={`/documents/${documentId}/drafts/${diff.to.id}`}>Open draft {diff.to.number}</Link>
          </div>
          {s.paragraphs.map((p) => (
            <div key={p.id} className="change">
              <div className="head"><span>{p.kind}</span></div>
              <div className="diff">
                <p className="inline-diff">
                  {wordDiff(p.before ?? '', p.after ?? '').map((seg, i) => {
                    if (seg.kind === 'removed') return <del key={i}>{seg.text}</del>;
                    if (seg.kind === 'added') return <ins key={i}>{seg.text}</ins>;
                    return <Fragment key={i}>{seg.text}</Fragment>;
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
